/**
 * F40 (Wave 2 Track D — admin) — platform-wide promotion analytics.
 *
 * Route: `/promotions/analytics`
 * Permission: `promotions:read` (super_admin + finance + support +
 *             content_moderator).
 *
 * Surface: a KPI Stat row (total redemptions / total discount / unique players /
 * suspended), a Line chart of redemptions over time, a Bar chart of the top
 * promotions by redemption count, and a top-promotions Table.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Chart,
  CurrencyDisplay,
  EmptyState,
  Skeleton,
  Stat,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { adminPromotionsApi, type AdminPromotionAnalyticsResponse } from '../../api/promotions';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';

type TopPromoRow = AdminPromotionAnalyticsResponse['topPromotions'][number];

export function AdminPromotionAnalyticsPage() {
  const { t, locale } = useI18n();

  const [data, setData] = useState<AdminPromotionAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await adminPromotionsApi.getAnalytics());
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchData();
  }, [fetchData]);

  const redemptionsOverTime = useMemo(
    () => (data?.redemptionsByDay ?? []).map((r) => ({ day: r.day, count: r.count })),
    [data],
  );

  const topPromoChartData = useMemo(
    () =>
      (data?.topPromotions ?? []).map((r) => ({
        code: r.code ?? r.promotionId.slice(0, 8),
        redemptions: r.redemptions,
      })),
    [data],
  );

  const topPromoColumns = useMemo<readonly TableColumn<TopPromoRow>[]>(
    () => [
      {
        key: 'code',
        header: t('admin:promotion.analytics.colCode', { defaultValue: 'Code' }),
        cell: (row) => (
          <span className="font-mono text-[12.5px] text-ink">
            {row.code ?? row.promotionId.slice(0, 8)}
          </span>
        ),
      },
      {
        key: 'venue',
        header: t('admin:promotion.analytics.colVenue', { defaultValue: 'Venue' }),
        cell: (row) => <span className="font-mono text-[11px] text-ink-3">{row.venueId}</span>,
      },
      {
        key: 'redemptions',
        header: t('admin:promotion.analytics.colRedemptions', { defaultValue: 'Redemptions' }),
        align: 'end',
        cell: (row) => (
          <span className="text-[12px] text-ink-2 tabular-nums">{row.redemptions}</span>
        ),
      },
      {
        key: 'discount',
        header: t('admin:promotion.analytics.colDiscount', { defaultValue: 'Discount given' }),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay amount={row.discountMinor} currency="USD" locale={locale} />
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="promotion-analytics-page">
      <PageHeader
        title={t('admin:promotion.analytics.title', { defaultValue: 'Promotion analytics' })}
        subtitle={t('admin:promotion.analytics.subtitle', {
          defaultValue:
            'Platform-wide promotion performance: redemptions over time, status distribution, and the top promotions.',
        })}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="promotion-analytics-loading">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : !data ? (
        <EmptyState
          title={t('admin:promotion.analytics.empty', {
            defaultValue: 'No promotion analytics yet.',
          })}
        />
      ) : (
        <>
          {/* KPI row */}
          <section
            className="grid grid-cols-2 gap-3 md:grid-cols-4"
            data-testid="promotion-analytics-kpis"
          >
            <Stat
              label={t('admin:promotion.analytics.totalRedemptions', {
                defaultValue: 'Total redemptions',
              })}
              value={data.totalRedemptions.toLocaleString(locale)}
            />
            <Stat
              label={t('admin:promotion.analytics.totalDiscount', {
                defaultValue: 'Total discount given',
              })}
              value={
                <CurrencyDisplay amount={data.totalDiscountMinor} currency="USD" locale={locale} />
              }
            />
            <Stat
              label={t('admin:promotion.analytics.uniqueUsers', { defaultValue: 'Unique players' })}
              value={data.distinctUsers.toLocaleString(locale)}
            />
            <Stat
              label={t('admin:promotion.analytics.suspended', { defaultValue: 'Suspended' })}
              value={data.suspendedCount.toLocaleString(locale)}
            />
          </section>

          {/* Redemptions over time (line) */}
          <section className="space-y-2" data-testid="promotion-analytics-over-time">
            <Chart
              kind="line"
              data={redemptionsOverTime}
              xKey="day"
              series={[
                {
                  key: 'count',
                  label: t('admin:promotion.analytics.redemptionsOverTime', {
                    defaultValue: 'Redemptions over time',
                  }),
                },
              ]}
              title={t('admin:promotion.analytics.redemptionsOverTime', {
                defaultValue: 'Redemptions over time',
              })}
              height={260}
            />
          </section>

          {/* Top promos by redemptions (bar) */}
          <section className="space-y-2" data-testid="promotion-analytics-top-bar">
            <Chart
              kind="bar"
              data={topPromoChartData}
              xKey="code"
              series={[
                {
                  key: 'redemptions',
                  label: t('admin:promotion.analytics.topPromotions', {
                    defaultValue: 'Top promotions',
                  }),
                },
              ]}
              title={t('admin:promotion.analytics.topPromotions', {
                defaultValue: 'Top promotions',
              })}
              height={240}
            />
          </section>

          {/* Top promos table */}
          <section className="space-y-3" data-testid="promotion-analytics-top-table">
            <h2 className="text-[14px] font-semibold text-ink">
              {t('admin:promotion.analytics.topPromotions', { defaultValue: 'Top promotions' })}
            </h2>
            {data.topPromotions.length === 0 ? (
              <EmptyState
                title={t('admin:promotion.analytics.topEmpty', {
                  defaultValue: 'No redemptions recorded yet.',
                })}
              />
            ) : (
              <Table
                data={data.topPromotions as TopPromoRow[]}
                columns={topPromoColumns as TableColumn<TopPromoRow>[]}
                rowKey={(row) => row.promotionId}
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default AdminPromotionAnalyticsPage;
